import { useEffect, useMemo, useRef, useState } from "react";
import {
  MessageSquareText, ImagePlus, Sparkles, Loader2, Trash2, Check, X,
  Plus, Save, AlertTriangle, ClipboardPaste, FileText, Printer, Pencil,
  GraduationCap, BookOpen, Share2,
} from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/Bits";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { OrderPrint } from "@/components/OrderPrint";
import { shareOrderOnWhatsApp } from "@/lib/shareOrderPdf";

const num = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};
const rupee = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN")}`;
const todayStr = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const WA_HEADER = /^\s*\u200e?\[?\d{1,2}\/\d{1,2}\/\d{2,4},?\s+\d{1,2}:\d{2}(?::\d{2})?\s?(?:[ap]\.?m\.?)?\]?\s*[-–]?\s*[^:\n]{1,40}:\s/i;
export const cleanWhatsApp = (t) =>
  String(t || "")
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((l) => l.replace(WA_HEADER, "").replace(/\u200e|\u200f/g, "").replace(/<This message was edited>/i, "").trimEnd())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

async function readAllClipboardText(e) {
  const dt = e.clipboardData;
  if (!dt) return "";
  const parts = await Promise.all(
    Array.from(dt.items || [])
      .filter((it) => it.kind === "string" && it.type === "text/plain")
      .map((it) => new Promise((res) => it.getAsString(res)))
  );
  const joined = parts.filter(Boolean).join("\n");
  return joined || dt.getData("text/plain") || dt.getData("text") || "";
}

function matchRate(it, rateItems) {
  const sys = (it.system || "").toLowerCase();
  const typ = (it.type || "").toLowerCase();
  const dia = (it.dia || "").trim();
  const length = (it.length || "").trim();
  const thread = (it.thread || "FT").trim().toUpperCase();
  if (typ === "nut") {
    const nuts = rateItems.filter((r) => r.cat === "nut");
    const nd = dia.replace(/^[Mm]/, "").trim();
    const isInch = nd.includes("/") || nd.includes('"');
    const bare = nd.replace(/"/g, "");
    const wants = isInch ? [`${bare}NUT`, `${bare}"NUT`] : [`${bare}MNUT`];
    for (const w of wants) {
      const hit = nuts.find((r) => String(r.name).toUpperCase() === w.toUpperCase());
      if (hit) return hit;
    }
    return nuts.find((r) => String(r.dia).trim().replace(/"/g, "") === bare) || null;
  }
  const cat = sys === "inch" ? "inch" : "metric";
  return (
    rateItems.find(
      (r) =>
        r.cat === cat &&
        String(r.dia).trim() === dia &&
        String(r.len).trim() === length &&
        String(r.thread).trim().toUpperCase() === thread
    ) || null
  );
}

function sizeLabel(it) {
  if (it.type === "nut") return `${it.dia} NUT`;
  return `${it.dia} × ${it.length} ${it.thread || "FT"}`;
}

export function ImportOrderPage() {
  const [text, setText] = useState("");
  const [image, setImage] = useState(null); // {b64, name}
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null); // {party,date,order_basic_rate,items}
  const [rateItems, setRateItems] = useState([]);
  const [localBolt, setLocalBolt] = useState("");
  const [localNut, setLocalNut] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [teach, setTeach] = useState(true);
  const [saving, setSaving] = useState(false);
  const [orders, setOrders] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [printOrder, setPrintOrder] = useState(null);
  const [examples, setExamples] = useState([]);
  const [showExamples, setShowExamples] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/rates").then((r) => setRateItems(r.data.items || [])).catch(() => {});
    loadOrders();
    loadExamples();
  }, []);

  const loadOrders = () =>
    api.get("/import/orders").then((r) => setOrders(r.data || [])).catch(() => {});

  const loadExamples = () =>
    api.get("/import/examples").then((r) => setExamples(r.data || [])).catch(() => {});

  const pickImage = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setImage({ b64: reader.result, name: file.name || "pasted-image.png" });
    reader.readAsDataURL(file);
  };

  const appendText = (t) => {
    const c = cleanWhatsApp(t);
    if (!c) return 0;
    setText((prev) => (prev.trim() ? prev.trimEnd() + "\n" + c : c));
    return c.split("\n").filter((l) => l.trim()).length;
  };

  const onPaste = async () => {
    try {
      let gotImage = false;
      const texts = [];
      if (navigator.clipboard?.read) {
        const clipItems = await navigator.clipboard.read();
        for (const ci of clipItems) {
          const imgType = ci.types.find((t) => t.startsWith("image/"));
          if (imgType && !gotImage) {
            const blob = await ci.getType(imgType);
            pickImage(new File([blob], "pasted-image.png", { type: imgType }));
            gotImage = true;
            toast.success("Photo pasted");
            continue;
          }
          const tType = ci.types.includes("text/plain") ? "text/plain" : ci.types.find((t) => t.startsWith("text/"));
          if (tType) {
            const blob = await ci.getType(tType);
            let s = await blob.text();
            if (tType === "text/html") s = s.replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]+>/g, "");
            texts.push(s);
          }
        }
      }
      if (!texts.length && !gotImage) {
        const t = await navigator.clipboard.readText();
        if (t) texts.push(t);
      }
      const n = appendText(texts.join("\n"));
      if (n) toast.success(`Pasted ${texts.length > 1 ? texts.length + " messages · " : ""}${n} line${n > 1 ? "s" : ""}`);
      else if (!gotImage) toast.error("Clipboard is empty");
    } catch {
      toast.error("Clipboard blocked — allow Paste when asked, or long-press in the box");
    }
  };

  const onTextareaPaste = (e) => {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.type && it.type.startsWith("image/")) {
        const file = it.getAsFile();
        if (file) {
          e.preventDefault();
          pickImage(file);
          toast.success("Photo pasted");
          return;
        }
      }
    }
    e.preventDefault();
    readAllClipboardText(e).then((t) => {
      if (!appendText(t)) toast.error("Nothing to paste");
    });
  };

  const doParse = async () => {
    if (!text.trim() && !image) {
      toast.error("Paste an order or upload an image first");
      return;
    }
    setParsing(true);
    try {
      const { data } = await api.post("/import/parse", {
        text: cleanWhatsApp(text) || null,
        image_base64: image?.b64 || null,
      });
      setParsed({ ...data, party: data.party || "", address: data.address || "", gstin: data.gstin || "", transport: data.transport || "", date: data.date || todayStr() });
      const bb = data.order_basic_rate != null ? String(data.order_basic_rate) : "";
      setLocalBolt(bb);
      setLocalNut("");
      const matched = (data.items || []).filter((i) => i.matched).length;
      toast.success(`Parsed ${data.items?.length || 0} item(s) · ${matched} matched to rate list`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setParsing(false);
    }
  };

  const updateItem = (idx, patch) => {
    setParsed((p) => {
      const items = p.items.map((it, i) => {
        if (i !== idx) return it;
        const next = { ...it, ...patch };
        if ("dia" in patch || "length" in patch || "thread" in patch || "type" in patch || "system" in patch) {
          const m = matchRate(next, rateItems);
          next.matched = !!m;
          next.matched_name = m ? m.name : null;
          next.extra = m ? m.extra : 0;
        }
        return next;
      });
      return { ...p, items };
    });
  };

  const selectSize = (idx, name) => {
    const r = rateItems.find((x) => String(x.name) === name);
    setParsed((p) => {
      const items = p.items.map((it, i) => {
        if (i !== idx) return it;
        if (!r) return { ...it, matched_name: name, matched: false };
        const type = r.cat === "nut" ? "nut" : "bolt";
        const system = r.cat === "inch" ? "inch" : r.cat === "metric" ? "metric" : String(r.dia).includes("/") ? "inch" : "metric";
        return {
          ...it,
          type,
          system,
          dia: String(r.dia ?? ""),
          length: String(r.len ?? ""),
          thread: String(r.thread ?? (type === "nut" ? "" : "FT")),
          extra: r.extra ?? 0,
          matched_name: r.name,
          matched: true,
        };
      });
      return { ...p, items };
    });
  };

  const removeItem = (idx) =>
    setParsed((p) => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));

  const addItem = () =>
    setParsed((p) => ({
      ...p,
      items: [
        ...p.items,
        { raw: "", system: "metric", type: "bolt", dia: "", length: "", thread: "FT", qty_bags: 0, line_rate: null, extra: 0, matched: false, matched_name: null, plating: "", remarks: "" },
      ],
    }));

  const partyOptions = useMemo(
    () => [...new Set(orders.map((o) => (o.party || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [orders]
  );
  const transportOptions = useMemo(
    () => [...new Set(orders.map((o) => (o.transport || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [orders]
  );

  const sizeOptions = useMemo(() => {
    const order = { inch: 0, metric: 1, nut: 2 };
    return rateItems
      .filter((r) => r.enabled !== false)
      .map((r) => String(r.name))
      .sort((a, b) => {
        const ra = rateItems.find((x) => String(x.name) === a);
        const rb = rateItems.find((x) => String(x.name) === b);
        return (order[ra?.cat] ?? 9) - (order[rb?.cat] ?? 9);
      });
  }, [rateItems]);

  const hasRate = (it) => it.line_rate !== null && it.line_rate !== undefined && String(it.line_rate) !== "";
  const hasRates = !!parsed && parsed.items.some(hasRate);

  const finalRate = (it) => (hasRate(it) ? num(it.line_rate) : null);

  const buildExampleOutput = () => ({
    party: parsed.party || "",
    date: parsed.date || "",
    order_basic_rate: parsed.order_basic_rate ?? null,
    items: parsed.items.map((it) => ({
      raw: it.raw || "",
      system: it.system,
      type: it.type,
      dia: it.dia,
      length: it.length,
      thread: it.thread,
      qty_bags: num(it.qty_bags),
      line_rate: it.line_rate === "" ? null : it.line_rate ?? null,
      remarks: it.remarks || "",
    })),
  });

  const totals = useMemo(() => {
    if (!parsed) return { bags: 0, lines: 0, amount: 0 };
    return {
      bags: parsed.items.reduce((s, it) => s + num(it.qty_bags), 0),
      lines: parsed.items.length,
      amount: parsed.items.reduce((s, it) => s + num(it.qty_bags) * (finalRate(it) || 0), 0),
    };
  }, [parsed]);

  const resetForm = () => {
    setParsed(null);
    setText("");
    setImage(null);
    setEditingId(null);
    setDeliveryDate("");
  };

  const save = async () => {
    if (!parsed || parsed.items.length === 0) return;
    setSaving(true);
    try {
      const items = parsed.items.map((it) => ({ ...it, qty_bags: num(it.qty_bags), final_rate: hasRates ? finalRate(it) : null }));
      const payload = {
        party: parsed.party || "",
        date: parsed.date || "",
        delivery_date: deliveryDate || "",
        transport: (parsed.transport || "").trim(),
        address: (parsed.address || "").trim(),
        gstin: (parsed.gstin || "").trim().toUpperCase(),
        bolt_basic: hasRates ? null : num(localBolt) || null,
        nut_basic: hasRates ? null : num(localNut) || null,
        plating_charge: null,
        remarks: parsed.remarks || "",
        items,
        raw_input: text.trim() || (image ? `[image: ${image.name}]` : ""),
      };
      if (editingId) {
        await api.put(`/import/orders/${editingId}`, payload);
      } else {
        await api.post("/import/orders", payload);
      }
      if (teach && !editingId && text.trim()) {
        await api.post("/import/examples", { input: text.trim(), output: buildExampleOutput() }).catch(() => {});
        loadExamples();
      }
      toast.success(
        editingId ? "Order updated" : "Order saved" + (teach ? " · AI learned this format" : "")
      );
      resetForm();
      loadOrders();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const teachOnly = async () => {
    if (!parsed || !text.trim()) {
      toast.error("Teaching needs the original text message");
      return;
    }
    try {
      await api.post("/import/examples", { input: text.trim(), output: buildExampleOutput() });
      toast.success("Saved as a learning example — AI will use this format");
      loadExamples();
      resetForm();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const startEdit = (o) => {
    setEditingId(o.id);
    setParsed({
      party: o.party || "",
      date: o.date || "",
      transport: o.transport || "",
      address: o.address || "",
      gstin: o.gstin || "",
      order_basic_rate: null,
      remarks: o.remarks || "",
      items: (o.items || []).map((it) => {
        const m = matchRate(it, rateItems);
        return {
          ...it,
          line_rate: it.line_rate ?? null,
          matched: !!m,
          matched_name: m ? m.name : it.matched_name ?? null,
          extra: m ? m.extra : it.extra ?? 0,
        };
      }),
    });
    setDeliveryDate(o.delivery_date || "");
    setLocalBolt(o.bolt_basic != null ? String(o.bolt_basic) : "");
    setLocalNut(o.nut_basic != null ? String(o.nut_basic) : "");
    setText("");
    setImage(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.info(`Editing ${o.number}`);
  };

  const doPrint = (o) => {
    setPrintOrder(o);
    const prev = document.title;
    document.title = `ORDER ${o.number || ""} ${o.party || ""}`.trim();
    const restore = () => {
      document.title = prev;
      setPrintOrder(null);
      window.removeEventListener("afterprint", restore);
    };
    window.addEventListener("afterprint", restore);
    setTimeout(() => {
      window.print();
      setTimeout(restore, 1500);
    }, 100);
  };


  const shareOrder = (o) => shareOrderOnWhatsApp(o);

  const deleteOrder = async (id) => {
    try {
      await api.delete(`/import/orders/${id}`);
      loadOrders();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteExample = async (id) => {
    await api.delete(`/import/examples/${id}`).catch(() => {});
    loadExamples();
  };

  return (
    <div className="space-y-5" data-testid="import-order-page">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-black tracking-tight text-white">
          <MessageSquareText className="h-6 w-6 text-orange-500" /> IMPORT ORDER
        </h1>
        <p className="mt-1 text-sm text-slate-400">
          Paste a WhatsApp order or upload a screenshot / handwritten photo — AI reads it and builds a priced order.
        </p>
      </div>

      {/* Input */}
      <Card className="space-y-3 p-4">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400">
            <FileText className="h-3.5 w-3.5" /> Order message
          </span>
          {text && (
            <button onClick={() => setText("")} data-testid="clear-text-btn" className="text-[11px] font-bold text-slate-500 hover:text-red-400">
              Clear
            </button>
          )}
        </div>
        <button
          onClick={onPaste}
          data-testid="paste-btn"
          className="flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-emerald-500/50 bg-emerald-500/10 text-base font-bold text-emerald-300 active:bg-emerald-500/20"
        >
          <ClipboardPaste className="h-5 w-5" /> Paste from WhatsApp (messages or photo)
        </button>
        <p className="text-[11px] leading-relaxed text-slate-500">
          WhatsApp → select messages → <b>Share</b> (iPhone) / <b>Copy</b> (Android) → come back and tap the green button.
          Copy a photo the same way — party name, address &amp; GST are read from it. Date, time and sender names are removed automatically.
        </p>
        <textarea
          data-testid="order-text-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onPaste={onTextareaPaste}
          rows={8}
          placeholder={"Paste text OR a copied photo (Ctrl/Cmd+V)\n\ne.g.\n12X175 HALF THREAD 5 BAG BASIC RATE 3900\nM12 NUT 10 BAGS 4050\nFt 7/8:8 5"}
          className="w-full rounded-xl border border-slate-700 bg-slate-900/60 p-3 font-mono text-sm text-white outline-none focus:border-orange-500/60"
        />
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid="image-file-input"
            onChange={(e) => pickImage(e.target.files?.[0])}
          />
          <button
            onClick={() => fileRef.current?.click()}
            data-testid="upload-image-btn"
            className="flex h-12 items-center gap-1.5 rounded-xl border border-slate-700 bg-slate-800 px-4 text-sm font-bold text-slate-300 active:border-orange-500/60"
          >
            <ImagePlus className="h-5 w-5" /> {image ? "Change photo" : "Photo"}
          </button>
          {image && (
            <span className="flex items-center gap-1 text-xs text-emerald-400">
              <Check className="h-3.5 w-3.5" /> {image.name}
              <button onClick={() => setImage(null)} className="ml-1 text-slate-500 hover:text-red-400">
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          )}
          <Button
            onClick={doParse}
            disabled={parsing}
            data-testid="parse-order-btn"
            className="ml-auto h-12 flex-1 gap-2 rounded-xl bg-orange-600 px-6 text-base font-bold hover:bg-orange-500"
          >
            {parsing ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
            {parsing ? "Reading..." : "Parse Order"}
          </Button>
        </div>
      </Card>

      {/* Review */}
      {parsed && (
        <Card className="space-y-4 p-4" data-testid="review-section">
          {editingId && (
            <div className="flex items-center gap-2 rounded-lg border border-sky-500/40 bg-sky-500/10 px-3 py-1.5 text-xs font-bold text-sky-300" data-testid="editing-banner">
              <Pencil className="h-3.5 w-3.5" /> Editing saved order — changes will update it
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            <Field label="Party" value={parsed.party} onChange={(v) => setParsed((p) => ({ ...p, party: v }))} testid="party-input" list="party-names" placeholder="Type or pick party…" />
            <datalist id="party-names">
              {partyOptions.map((n) => (
                <option key={n} value={n} />
              ))}
            </datalist>
            <Field label="GST No" value={parsed.gstin} onChange={(v) => setParsed((p) => ({ ...p, gstin: v }))} testid="gstin-input" placeholder="GSTIN" />
            <div className="col-span-2 sm:col-span-3">
              <Field label="Address" value={parsed.address} onChange={(v) => setParsed((p) => ({ ...p, address: v }))} testid="address-input" placeholder="Party address" />
            </div>
            <Field label="Order Date" value={parsed.date} onChange={(v) => setParsed((p) => ({ ...p, date: v }))} testid="date-input" />
            <Field label="Transport" value={parsed.transport} onChange={(v) => setParsed((p) => ({ ...p, transport: v }))} testid="transport-input" list="transport-names" placeholder="Transporter…" />
            <datalist id="transport-names">
              {transportOptions.map((n) => (
                <option key={n} value={n} />
              ))}
            </datalist>
            <Field label="Delivery" value={deliveryDate} onChange={setDeliveryDate} testid="delivery-input" />
            {!hasRates && (
              <div className="col-span-2 grid grid-cols-2 gap-3 sm:col-span-1">
                <Field label="Basic Rate ₹" value={localBolt} onChange={setLocalBolt} testid="bolt-basic-input" />
                <Field label="Nut Basic ₹" value={localNut} onChange={setLocalNut} testid="nut-basic-input" />
              </div>
            )}
          </div>
          <p className="text-[11px] text-slate-500" data-testid="rate-mode-hint">
            {hasRates
              ? "Rates found in order — PDF shows Rate/Bag + Amount."
              : "No rates in order — only the Basic Rate prints in the PDF header; no rate or amount columns."}
          </p>

          <datalist id="rate-sizes">
            {sizeOptions.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>
          <div className="space-y-2.5">
            {parsed.items.map((it, idx) => (
              <div
                key={idx}
                data-testid={`review-row-${idx}`}
                className={`rounded-2xl border bg-slate-900/50 p-3 ${it.matched ? "border-slate-700" : "border-amber-500/60"}`}
              >
                {/* Size + delete */}
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <input
                      list="rate-sizes"
                      value={it.matched_name ?? sizeLabel(it)}
                      onChange={(e) => selectSize(idx, e.target.value)}
                      data-testid={`size-${idx}`}
                      placeholder="Tap to pick size…"
                      className={`h-12 w-full rounded-xl bg-slate-800 px-3 text-base font-bold text-white outline-none focus:ring-2 focus:ring-orange-500/70 ${it.matched ? "" : "ring-2 ring-amber-500/60"}`}
                    />
                  </div>
                  <button
                    onClick={() => removeItem(idx)}
                    className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-slate-700 text-slate-400 active:bg-red-500/10 active:text-red-400"
                    data-testid={`remove-row-${idx}`}
                    aria-label="Remove line"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>

                {/* Plating / Bags / Rate */}
                <div className="mt-2.5 grid grid-cols-3 gap-2">
                  <label className="block">
                    <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">Plating</span>
                    <select
                      value={it.plating ?? ""}
                      onChange={(e) => updateItem(idx, { plating: e.target.value })}
                      className="h-11 w-full rounded-xl bg-slate-800 px-2 text-sm font-semibold text-white outline-none focus:ring-2 focus:ring-orange-500/70"
                      data-testid={`plating-${idx}`}
                    >
                      <option value="">Self</option>
                      <option value="ZINC">Zinc (GI)</option>
                      <option value="GOLDEN">Golden</option>
                      <option value="BLACK">Black</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">Bags</span>
                    <input
                      inputMode="numeric"
                      value={it.qty_bags}
                      onChange={(e) => updateItem(idx, { qty_bags: e.target.value })}
                      data-testid={`bags-${idx}`}
                      className="h-11 w-full rounded-xl bg-slate-800 px-3 text-center text-base font-bold text-white outline-none focus:ring-2 focus:ring-orange-500/70"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">Rate</span>
                    <input
                      inputMode="numeric"
                      value={it.line_rate ?? ""}
                      onChange={(e) => updateItem(idx, { line_rate: e.target.value === "" ? null : e.target.value })}
                      placeholder="—"
                      data-testid={`rate-${idx}`}
                      className="h-11 w-full rounded-xl bg-slate-800 px-3 text-center text-base font-bold text-white outline-none placeholder:text-slate-600 focus:ring-2 focus:ring-orange-500/70"
                    />
                  </label>
                </div>

                {/* Amount */}
                <div className="mt-2 flex items-center justify-between">
                  {!it.matched ? (
                    <span className="flex items-center gap-1 text-[11px] font-bold text-amber-400" data-testid={`unmatched-${idx}`}>
                      <AlertTriangle className="h-3.5 w-3.5" /> Pick a valid size
                    </span>
                  ) : hasRates && finalRate(it) != null ? (
                    <span className="text-[11px] text-slate-500">{num(it.qty_bags)} bags × {rupee(finalRate(it))}</span>
                  ) : (
                    <span className="text-[11px] text-slate-500">{num(it.qty_bags)} bags</span>
                  )}
                  {hasRates && (
                    <span className="font-mono text-lg font-black text-emerald-400" data-testid={`amount-${idx}`}>
                      {finalRate(it) != null ? rupee(num(it.qty_bags) * finalRate(it)) : "—"}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <button onClick={addItem} data-testid="add-row-btn" className="flex h-12 w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-700 text-sm font-bold text-orange-400 active:bg-orange-500/10">
            <Plus className="h-5 w-5" /> Add line
          </button>

          {/* Grand total */}
          <div className="flex items-center justify-between rounded-xl bg-slate-800/70 px-4 py-3">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">{totals.lines} lines · {totals.bags} bags</span>
            {hasRates ? (
              <span className="font-mono text-xl font-black text-emerald-400" data-testid="grand-total">{rupee(totals.amount)}</span>
            ) : (
              <span className="text-xs font-bold text-orange-400" data-testid="grand-total">Basic {localBolt ? rupee(localBolt) : "—"}</span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {!editingId && (
              <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" checked={teach} onChange={(e) => setTeach(e.target.checked)} data-testid="teach-checkbox" className="h-4 w-4 accent-orange-500" />
                Teach AI this format
              </label>
            )}
            <div className="ml-auto flex items-center gap-2">
              {editingId ? (
                <Button onClick={resetForm} variant="ghost" data-testid="cancel-edit-btn" className="h-12 gap-1.5 rounded-xl text-slate-300 hover:text-white">
                  <X className="h-5 w-5" /> Cancel
                </Button>
              ) : (
                text.trim() && (
                  <Button onClick={teachOnly} data-testid="teach-only-btn" variant="outline" className="h-12 gap-1.5 rounded-xl border-slate-600 text-slate-200 hover:border-orange-500/60">
                    <GraduationCap className="h-5 w-5" /> Teach only
                  </Button>
                )
              )}
              <Button onClick={save} disabled={saving} data-testid="save-order-btn" className="h-12 flex-1 gap-2 rounded-xl bg-emerald-600 px-6 text-base font-bold hover:bg-emerald-500">
                {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />} {editingId ? "Update Order" : "Save Order"}
              </Button>
            </div>
          </div>
          {parsed.items.some((i) => !i.matched) && (
            <p className="flex items-center gap-1.5 text-[11px] text-amber-400">
              <AlertTriangle className="h-3.5 w-3.5" /> Amber rows weren't found in the 4.6 rate list — check the size.
            </p>
          )}
        </Card>
      )}

      {/* Saved orders */}
      {orders.length > 0 && (
        <Card className="p-4" data-testid="saved-orders">
          <h2 className="mb-2 text-sm font-bold uppercase tracking-wider text-slate-400">Saved Imported Orders</h2>
          <div className="space-y-1.5">
            {orders.map((o) => (
              <div key={o.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-2 text-sm" data-testid={`order-${o.id}`}>
                <span className="font-mono text-xs text-orange-400">{o.number}</span>
                <span className="font-bold text-white">{o.party || "—"}</span>
                <span className="text-xs text-slate-400">{o.date}</span>
                <span className="ml-auto text-xs text-slate-300">{o.items?.length || 0} items · {o.total_bags} bags</span>
                <button onClick={() => shareOrder(o)} title="Send as WhatsApp text" className="text-emerald-500 hover:text-emerald-400" data-testid={`whatsapp-order-${o.id}`}>
                  <Share2 className="h-4 w-4" />
                </button>
                <button onClick={() => doPrint(o)} title="Download / Print PDF" className="text-slate-400 hover:text-orange-400" data-testid={`pdf-order-${o.id}`}>
                  <Printer className="h-4 w-4" />
                </button>
                <button onClick={() => startEdit(o)} title="Edit order" className="text-slate-400 hover:text-sky-400" data-testid={`edit-order-${o.id}`}>
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => deleteOrder(o.id)} title="Delete" className="text-slate-600 hover:text-red-400" data-testid={`delete-order-${o.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Learned examples manager */}
      <Card className="p-4" data-testid="examples-card">
        <button
          onClick={() => setShowExamples((s) => !s)}
          data-testid="toggle-examples-btn"
          className="flex w-full items-center gap-2 text-sm font-bold uppercase tracking-wider text-slate-400 hover:text-slate-200"
        >
          <BookOpen className="h-4 w-4" /> Learned Examples ({examples.length})
          <span className="ml-auto text-[11px] font-normal normal-case text-slate-500">
            {showExamples ? "hide" : "show"}
          </span>
        </button>
        {showExamples && (
          <div className="mt-3 space-y-1.5">
            <p className="text-[11px] text-slate-500">
              These are the order formats the AI has learned. Paste a new order above, parse &amp; correct it, then hit "Teach only" (or keep the checkbox on when saving) to add more.
            </p>
            {examples.length === 0 && <p className="text-xs text-slate-500">No examples yet.</p>}
            {examples.map((ex) => (
              <div key={ex.id} className="flex items-start gap-3 rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-2" data-testid={`example-${ex.id}`}>
                <pre className="flex-1 whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-slate-300">{ex.input}</pre>
                <span className="shrink-0 text-[10px] text-slate-500">{ex.seed ? "seed" : (ex.output?.items?.length || 0) + " items"}</span>
                <button onClick={() => deleteExample(ex.id)} className="shrink-0 text-slate-600 hover:text-red-400" data-testid={`delete-example-${ex.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <OrderPrint order={printOrder} />
    </div>
  );
}

function Field({ label, value, onChange, testid, list, placeholder }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</span>
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        list={list}
        placeholder={placeholder}
        className="h-11 rounded-xl border-slate-700 bg-slate-800 text-base text-white focus-visible:ring-orange-500/60"
      />
    </label>
  );
}

function Cell({ value, onChange, w = "w-16", right, disabled }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={`${w} rounded bg-slate-900/60 px-1.5 py-1 text-xs text-white outline-none focus:ring-1 focus:ring-orange-500/60 disabled:opacity-30 ${right ? "text-right" : ""}`}
    />
  );
}

export default ImportOrderPage;
